// Cotizador de créditos del Portal Vendedor, con el modelo y las piezas del paso «Cotizar» del wizard.
// Producto del catálogo o monto libre; nivel del plan de la tienda; plazo, y enganche o pago fijo.
import { useMemo, useState, type ReactNode } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { ClientLevelEnum } from '@fiado/type-kit/bin/loanScoring/enums/ClientLevelEnum';
import { tierLabel, type TierValue } from '@/shared/patterns/Pills/tierPresentation';
import { useCatalog } from '@/domains/sales/wizard/api/useWizard';
import { useSellerContext } from '@/domains/sales/wizard/sellerContext';
import type { CatalogItem } from '@/domains/sales/wizard/types';
import { AmountInput } from '@/domains/sales/wizard/components/AmountInput';
import { ChoiceChip } from '@/domains/sales/wizard/components/ChoiceChip';
import { LinkedQuoteCards } from '@/domains/sales/wizard/components/LinkedQuoteCards';
import {
    WizardCard,
    WizardCardBody,
    WizardCardHead,
} from '@/domains/sales/wizard/components/WizardCard';
import { itemDisplayName } from '@/shared/lib/format/itemDisplayName';
import { formatMXN } from '@/shared/lib/format/money';
import { NO_CELL_VALUE } from '@/shared/patterns/DataTable';
import { ScreenFrame } from '@/shared/patterns/ScreenFrame';
import { ProductPicker } from './components/ProductPicker';
import { QuoteResult } from './components/QuoteResult';
import { defaultPhone, quoterPhones } from './lib/quoterProducts';
import { formatRate, planLabel } from './lib/quoterPlans';
import { useQuote, type LinkedQuote, type Quote } from './useQuote';

type ProductMode = 'catalog' | 'free';

/** Chips sin plan de la tienda: los niveles del mock, quietos y sin tasa. */
const EMPTY_LEVELS: TierValue[] = [
    ClientLevelEnum.BRONZE,
    ClientLevelEnum.SILVER,
    ClientLevelEnum.GOLD,
    'employee',
];

const NOTICE =
    'rounded-[10px] border border-warning/40 bg-warning/10 px-4 py-3 text-[12.5px] leading-relaxed';

export function SalesQuoter() {
    const navigate = useNavigate();
    const { storeId } = useSellerContext();
    const catalog = useCatalog('portal');
    const phones = useMemo(() => quoterPhones(catalog.data?.items ?? []), [catalog.data]);

    const [mode, setMode] = useState<ProductMode>('catalog');
    const [selectedSku, setSelectedSku] = useState<string | null>(null);
    const [autoPicked, setAutoPicked] = useState(false);
    const [freeAmount, setFreeAmount] = useState(0);

    // Como en el mock, arranca con el teléfono más popular ya elegido (si hay, con piezas).
    const firstPhone = defaultPhone(phones);
    if (!autoPicked && firstPhone) {
        setAutoPicked(true);
        setSelectedSku(firstPhone.sku);
    }

    const selected: CatalogItem | null = phones.find((item) => item.sku === selectedSku) ?? null;
    const freeCents = Math.max(0, Math.round(freeAmount * 100));
    const priceCents = mode === 'catalog' ? (selected?.cashPriceCents ?? 0) : freeCents;
    const quote = useQuote(storeId, priceCents, mode === 'catalog' ? selected?.sku : undefined);

    const convert = () => {
        if (mode === 'catalog' && selected) {
            void navigate({ to: '/wizard', search: { sku: selected.sku } });
            return;
        }
        void navigate({ to: '/wizard' });
    };

    return (
        <ScreenFrame height="grow" className="bg-background">
            <div className="mx-auto grid w-full max-w-[1180px] grid-cols-1 items-start gap-4 px-4 pb-10 pt-4 sm:px-5 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
                <WizardCard className="max-w-none lg:max-w-none">
                    <WizardCardHead
                        eyebrow="Cotizador"
                        title="Arma la cotización"
                        sub="Elige el equipo y el nivel del cliente, y ajusta el crédito."
                    />
                    <WizardCardBody className="flex flex-col gap-5">
                        <Section id="quoter-product-title" title="Producto">
                            <div
                                role="group"
                                aria-label="Cómo cotizar"
                                className="flex flex-wrap gap-2"
                            >
                                <ChoiceChip
                                    selected={mode === 'catalog'}
                                    onSelect={() => {
                                        setMode('catalog');
                                    }}
                                >
                                    Del catálogo
                                </ChoiceChip>
                                <ChoiceChip
                                    selected={mode === 'free'}
                                    onSelect={() => {
                                        setMode('free');
                                    }}
                                >
                                    Monto libre
                                </ChoiceChip>
                            </div>
                            {mode === 'catalog' ? (
                                <ProductPicker
                                    phones={phones}
                                    selected={selected}
                                    onSelect={(item) => {
                                        setSelectedSku(item.sku);
                                    }}
                                    onClear={() => {
                                        setSelectedSku(null);
                                    }}
                                    isLoading={catalog.isLoading}
                                    isError={catalog.isError}
                                    onRetry={() => void catalog.refetch()}
                                />
                            ) : (
                                <div className="flex flex-col gap-1.5">
                                    <label
                                        htmlFor="quoter-free-amount"
                                        className="text-[12.5px] font-semibold"
                                    >
                                        Monto del equipo
                                    </label>
                                    <AmountInput
                                        id="quoter-free-amount"
                                        value={freeAmount}
                                        onValueChange={setFreeAmount}
                                        className="h-11 w-full rounded-[10px] text-right text-[20px] font-semibold"
                                    />
                                    <p className="text-[11.5px] text-muted-foreground">
                                        Escribe el precio de contado del equipo o del producto que
                                        quieras cotizar.
                                    </p>
                                </div>
                            )}
                        </Section>

                        <hr className="border-border" />

                        <Section id="quoter-level-title" title="Nivel del cliente">
                            <LevelChips quote={quote} />
                            <p className="text-[11.5px] text-muted-foreground">
                                El nivel determina la tasa, el enganche mínimo y los plazos
                                disponibles.
                            </p>
                        </Section>

                        <hr className="border-border" />

                        <Section id="quoter-adjust-title" title="Ajusta el crédito">
                            <AdjustCredit quote={quote} hasPrice={priceCents > 0} />
                        </Section>
                    </WizardCardBody>
                </WizardCard>

                <QuoteResult
                    priceCents={priceCents}
                    productName={mode === 'catalog' && selected ? itemDisplayName(selected) : null}
                    plan={quote.plan}
                    status={quote.status}
                    numbers={quote.numbers}
                    savings={quote.savings}
                    terms={quote.terms}
                    selectedTerm={quote.termWeeks}
                    onSelectTerm={(weeks) => {
                        quote.linked?.change({ kind: 'weeks', weeks });
                    }}
                    onConvert={convert}
                />
            </div>
        </ScreenFrame>
    );
}

function Section({ id, title, children }: { id: string; title: string; children: ReactNode }) {
    return (
        <section aria-labelledby={id} className="flex flex-col gap-2.5">
            <h2 id={id} className="text-[13.5px] font-bold">
                {title}
            </h2>
            {children}
        </section>
    );
}

/** Plazo y las tarjetas ligadas del wizard; sin plan o sin enganche posible, el aviso que toca. */
function AdjustCredit({ quote, hasPrice }: { quote: Quote; hasPrice: boolean }) {
    const { linked, status } = quote;
    if (status.kind === 'no-feasible') {
        return (
            <div role="alert" className={NOTICE}>
                {status.message}
            </div>
        );
    }
    if (linked === null) {
        return (
            <p className="text-[12.5px] text-muted-foreground">
                {hasPrice
                    ? 'El enganche, el plazo y el pago semanal aparecen con el plan del nivel.'
                    : 'Elige un producto o escribe un monto para ajustar el crédito.'}
            </p>
        );
    }
    return (
        <LinkedCards
            linked={linked}
            notice={linked.notice ?? (status.kind === 'error' ? status.message : null)}
            tna={quote.numbers?.tna ?? quote.plan?.tnaAnnual ?? 0}
            price={quote.numbers?.price ?? 0}
        />
    );
}

function LinkedCards({
    linked,
    notice,
    tna,
    price,
}: {
    linked: LinkedQuote;
    notice: string | null;
    tna: number;
    price: number;
}) {
    const share = price > 0 ? Math.round((linked.down / price) * 100) : 0;
    return (
        <LinkedQuoteCards
            fixed={linked.fixed}
            onFix={linked.fix}
            down={linked.down}
            minDown={linked.minDown}
            maxDown={linked.maxDown}
            rangeLabel={
                <span className="flex justify-between gap-2">
                    {linked.promoLoweredMinDown !== null ? (
                        <span>
                            Mín.{' '}
                            <s className="opacity-70">{formatMXN(linked.promoLoweredMinDown)}</s>{' '}
                            <span className="font-bold text-promo">
                                {formatMXN(linked.minDown)}
                            </span>
                        </span>
                    ) : (
                        <span>Mín. {formatMXN(linked.minDown)}</span>
                    )}
                    <span>Máx. {formatMXN(linked.maxDown)}</span>
                </span>
            }
            downShare={`${String(share)}% del precio`}
            onDownChange={linked.setDown}
            onDownCommit={(pesos) => {
                linked.change({ kind: 'down', down: pesos });
            }}
            weeks={linked.weeks}
            terms={linked.terms}
            onWeeksCommit={(weeks) => {
                linked.change({ kind: 'weeks', weeks });
            }}
            pay={linked.pay}
            payTarget={linked.payTarget}
            payRange={linked.payRange}
            onPayCommit={(pay) => {
                linked.change({ kind: 'pay', pay });
            }}
            payTerms={`${String(linked.weeks)} cuotas fijas · tasa anual ${String(Math.round(tna * 100))} %`}
            notice={notice}
        />
    );
}

/** Un chip por plan activo de la tienda con su TNA; sin planes, los niveles del mock vacíos. */
function LevelChips({ quote }: { quote: Quote }) {
    const chips =
        quote.plans.length === 0
            ? EMPTY_LEVELS.map((value) => ({
                  key: String(value),
                  label: tierLabel(value, 'short'),
                  rate: NO_CELL_VALUE,
                  selected: value === ClientLevelEnum.GOLD,
                  onSelect: undefined,
              }))
            : quote.plans.map((plan) => ({
                  key: plan.planId,
                  label: planLabel(plan),
                  rate: formatRate(plan.tnaAnnual),
                  selected: plan.planId === quote.plan?.planId,
                  onSelect: () => {
                      quote.selectPlan(plan.planId);
                  },
              }));
    return (
        <div role="group" aria-label="Nivel del cliente" className="grid grid-cols-4 gap-2">
            {chips.map((chip) => (
                <ChoiceChip
                    key={chip.key}
                    selected={chip.selected}
                    disabled={chip.onSelect === undefined}
                    onSelect={chip.onSelect ?? (() => undefined)}
                    className="px-2 py-1.5"
                >
                    {chip.label}
                    <span className="block text-[11px] font-medium opacity-75">{`TNA ${chip.rate}`}</span>
                </ChoiceChip>
            ))}
        </div>
    );
}
